import styled, { useTheme } from "styled-components";
import UserAvatar from "common/Avatars/UserAvatar";
import React, { CSSProperties } from "react";
import Text from "common/Typography/Text";
import { typography } from "@shared/common/Themes/typography";
import { OnboardingStyles } from "features/onboarding/common/styles";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { Telegram } from "common/Icons/Icons";
import CloseOutlined from "@ant-design/icons/CloseOutlined";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import { SharedTelegramUserPreviewProps } from "@shared/features/onboarding/types";
import { CONNECT_TELEGRAM_INFO } from "@shared/features/onboarding/constants";
import { AnalyticsPageName, LanguageNameSpaces } from "@shared/types";
import Modal from "common/Modal/Modal";
import { useTelegramUserPreview } from "@shared/features/onboarding/hooks";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface TelegramUserPreviewProps extends SharedTelegramUserPreviewProps {
  style?: CSSProperties;
}

const TelegramUserPreview = ({
  horizontal,
  telegramUser,
  style,
  isLoading,
  showRemoveUuidButton,
  showInfoButton,
  showTelegramIcon,
  showingContinueWithoutTelegram,
  removeUuid,
}: TelegramUserPreviewProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const {
    openRemoveUuidConfirmationModal,
    removeUuidModalData,
    tooltipNotYourAccountText,
  } = useTelegramUserPreview(
    removeUuid,
    showRemoveUuidButton,
    showingContinueWithoutTelegram
  );
  const theme = useTheme();
  const avatarSize = 70;

  const infoTooltip = (
    <HelperTooltip
      analyticsPageName={AnalyticsPageName.CONNECT_EXTERNAL_ACCOUNT}
      analyticsFieldName="Telegram Account"
      description={
        <>
          <div>{t(CONNECT_TELEGRAM_INFO)}</div>
          {tooltipNotYourAccountText && (
            <div style={{ marginTop: 8 }}>{tooltipNotYourAccountText}</div>
          )}
        </>
      }
    />
  );

  const confirmRemoveUuidModal = (
    <Modal
      title={removeUuidModalData.title}
      open={removeUuidModalData.isVisible}
      onCancel={removeUuidModalData.onCancel}
      onOk={removeUuidModalData.onOk}
    >
      <Text>{removeUuidModalData.description[0]}</Text>
      <Text style={{ marginTop: 4 }}>{removeUuidModalData.description[1]}</Text>
    </Modal>
  );

  return (
    <Wrapper style={style} $horizontal={horizontal}>
      <LoadingSpinnerOverlay isLoading={isLoading}>
        {showRemoveUuidButton && (
          <CloseButton onClick={openRemoveUuidConfirmationModal} />
        )}
        {showInfoButton && <InfoWrapper>{infoTooltip}</InfoWrapper>}
        <ContentWrapper $horizontal={horizontal}>
          <div
            style={{
              position: "relative",
              display: "flex",
              justifyContent: "center",
            }}
          >
            {telegramUser && (
              <>
                <UserAvatar
                  user={telegramUser}
                  avatarSize={avatarSize}
                  fallbackInitialsSize={1}
                  isTelegramUser={true}
                />
                {showTelegramIcon && (
                  <TelegramUserWrapper>
                    <Telegram
                      $size={"20px"}
                      fillColor={theme.colors.primaryBlue}
                    />
                  </TelegramUserWrapper>
                )}
              </>
            )}
          </div>
          <UserNameWrapper $horizontal={horizontal}>
            <Text
              $size={typography.fontSizes.L}
              $weight={typography.fontWeights.MEDIUM}
              $ellipsis={true}
            >
              {(telegramUser?.first_name || "").concat(
                " ",
                telegramUser?.last_name || ""
              )}
            </Text>
            {telegramUser?.username && (
              <Text type="secondary" $ellipsis={true}>
                @{telegramUser?.username}
              </Text>
            )}
          </UserNameWrapper>
        </ContentWrapper>
      </LoadingSpinnerOverlay>
      {confirmRemoveUuidModal}
    </Wrapper>
  );
};

export default TelegramUserPreview;

const Wrapper = styled(OnboardingStyles.InfoBackground)<{
  $horizontal?: boolean;
}>`
  margin: 24px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  ${(p) => p.$horizontal && `padding-bottom: 20px;`};
  width: 100%;
  position: relative;
`;
const ContentWrapper = styled.div<{ $horizontal?: boolean }>`
  display: flex;
  flex-direction: ${(p) => (p.$horizontal ? "row" : "column")};
  ${(p) =>
    p.$horizontal &&
    `
    column-gap: 20px;
  `}
`;
const TelegramUserWrapper = styled.div`
  position: absolute;
  right: -6px;
  bottom: -6px;
  border-radius: 50%;
  background-color: ${(p) => p.theme.colors.cardBgColor};
  box-shadow: ${(p) =>
    p.theme.isDarkMode
      ? "rgb(0 0 0 / 0.35) 0 3px 12px"
      : "rgba(0, 0, 0, 0.15) 0 2px 8px"};
  height: 28px;
  width: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: 2px;
`;
const UserNameWrapper = styled.div<{ $horizontal?: boolean }>`
  display: flex;
  flex-direction: column;
  margin-top: 8px;
  text-align: ${(p) => (p.$horizontal ? "flex-start" : "center")};
  justify-content: ${(p) => (p.$horizontal ? "center" : "flex-start")};
  max-width: 160px;
`;
const CloseButton = styled(CloseOutlined)`
  position: absolute;
  right: 12px;
  top: 12px;
  font-size: 20px;
  color: ${(p) => p.theme.colors.secondaryGrey};
`;
const InfoWrapper = styled.div`
  position: absolute;
  left: 4px;
  top: 7px;
  font-size: 20px;
  color: ${(p) => p.theme.colors.secondaryGrey};
`;
